import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { newId, useAppStore } from '@restorant/core';
import type { Category } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useT } from '../i18n';
import { Icon } from '../icons/Icon';
import { ICON_PATHS } from '../icons/generated';
import type { IconName } from '../icons/Icon';
import { Sheet, SheetButton, Toggle } from './Sheet';
import { T } from './Text';
import { TextField } from './TextField';

/** Only the food icons are offered — the rest are UI chrome. */
const CATEGORY_ICONS = Object.keys(ICON_PATHS).filter((k) =>
  k.startsWith('cat-'),
) as IconName[];

function IconPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const colors = useColors();
  const t = useT();
  return (
    <View style={{ gap: 8 }}>
      <T size={11.5} weight={600} color={colors.textFaint}>
        {t('icon')}
      </T>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {CATEGORY_ICONS.map((name) => {
          const active = name === value;
          return (
            <Pressable
              key={name}
              onPress={() => onChange(name)}
              style={{
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: radius.md,
                borderWidth: 1,
                borderColor: active ? colors.gold : colors.line,
                backgroundColor: active ? colors.surface2 : 'transparent',
              }}
            >
              <Icon name={name} size={20} color={active ? colors.gold : colors.textDim} />
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

/** Slug used as the primary key. Stable, readable, and safe in a URL. */
function slugify(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  return base || 'cat-' + newId().slice(0, 8);
}

export function CategoryEditor({
  category,
  visible,
  onClose,
}: {
  /** null = creating a new one. */
  category: Category | null;
  visible: boolean;
  onClose: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const categories = useAppStore((s) => s.categories);
  const upsertCategory = useAppStore((s) => s.upsertCategory);
  const deleteCategory = useAppStore((s) => s.deleteCategory);
  const showToast = useAppStore((s) => s.showToast);

  const [name, setName] = useState('');
  const [nameHi, setNameHi] = useState('');
  const [nameGu, setNameGu] = useState('');
  const [icon, setIcon] = useState('cat-special');
  const [active, setActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setName(category?.name ?? '');
    setNameHi(category?.nameHi ?? '');
    setNameGu(category?.nameGu ?? '');
    setIcon(category?.icon ?? 'cat-special');
    setActive(category?.active ?? true);
    setError(null);
    setConfirmDelete(false);
  }, [category, visible]);

  const save = () => {
    if (!name.trim()) return setError(t('name_required'));

    // Editing keeps the original id: order history and offers point at it.
    let id = category?.id;
    if (!id) {
      id = slugify(name);
      const taken = new Set(categories.map((c) => c.id));
      let n = 2;
      while (taken.has(id)) id = `${slugify(name)}-${n++}`;
    }

    upsertCategory({
      id,
      name: name.trim(),
      nameHi: nameHi.trim(),
      nameGu: nameGu.trim(),
      icon,
      sortOrder: category?.sortOrder ?? (categories.length + 1) * 10,
      active,
    });
    showToast(t('saved_toast'));
    onClose();
  };

  const remove = () => {
    if (!category) return;
    if (!confirmDelete) return setConfirmDelete(true);
    deleteCategory(category.id);
    showToast(t('deleted_toast'));
    onClose();
  };

  return (
    <Sheet
      visible={visible}
      title={category ? t('edit_category') : t('add_category')}
      onClose={onClose}
      footer={
        <>
          {category ? (
            <SheetButton
              label={confirmDelete ? t('delete_category_confirm') : t('delete')}
              icon="trash"
              tone="danger"
              onPress={remove}
            />
          ) : null}
          <SheetButton label={t('save')} icon="check" tone="primary" onPress={save} />
        </>
      }
    >
      <TextField testID="cat-name" label={t('name_en')} value={name} onChangeText={setName} />
      <TextField testID="cat-name-hi" label={t('name_hi')} value={nameHi} onChangeText={setNameHi} />
      <TextField testID="cat-name-gu" label={t('name_gu')} value={nameGu} onChangeText={setNameGu} />
      <IconPicker value={icon} onChange={setIcon} />
      <Toggle label={t('active_label')} value={active} onChange={setActive} />
      {error ? (
        <T size={12} color={colors.spice}>
          {error}
        </T>
      ) : null}
    </Sheet>
  );
}
